import ActionButton from "@/components/buttons/ActionButton";
import { BooleanFilterSelect } from "@/components/form/components/BooleanFilterSelect";
import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Flex, Form, Input, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { DEFAULT_FILTER_CLIENTS_QUERY_PARAMS } from "../../api";
import { ClientPremiumType } from "../../enums";
import { ClientFilterPageRequest } from "../../types";
import { ExtendedClientFilterPageRequest } from "./ClientExtendedFilterFormFields";
import { ClientExtendedFilterModalForm } from "./ClientExtendedFilterModalForm";

interface Props {
  searchParams: ClientFilterPageRequest;
}

const ClientListFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form] = Form.useForm<ExtendedClientFilterPageRequest>();
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      createdAtRange:
        searchParams.createdAtMin && searchParams.createdAtMax
          ? [searchParams.createdAtMin, searchParams.createdAtMax]
          : undefined,
    });
  }, [searchParams, form]);

  const handleFilterClear = () => {
    form.resetFields();
    onFilterSubmit(form.getFieldsValue());
  };

  const onFilterSubmit = ({ createdAtRange, ...values }: ExtendedClientFilterPageRequest) => {
    const filter: ClientFilterPageRequest = {
      ...values,
      createdAtMin: createdAtRange?.[0],
      createdAtMax: createdAtRange?.[1],
    };

    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_FILTER_CLIENTS_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("client.titles.list")}</h2>}
      extra={
        <ActionButton color="danger" size="middle" variant="outlined" icon="reload" onClick={handleFilterClear}>
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form form={form} onFinish={onFilterSubmit} layout="vertical" name="clientFilterViewForm">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(4, 1fr) `,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <Form.Item
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("client.helpers.searchHint")} />
          </Form.Item>

          {isTopAgentPlus && (
            <Form.Item name="premiumType" label={t("client.enums.premiumType._label")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.values(ClientPremiumType).map((premiumType) => ({
                  value: premiumType,
                  label: t(`client.enums.premiumType.${premiumType}`),
                }))}
              />
            </Form.Item>
          )}

          <Form.Item name="managedByIds" label={t("client.attrs.managedBy")} rules={[validations.none]}>
            <AgentSelect allowClear isMultiple />
          </Form.Item>

          <Form.Item
            name="hasMobileApp"
            label={t("client.enums.booleanFilterHasMobileApp._label")}
            rules={[validations.none]}
          >
            <BooleanFilterSelect translationPath="client.enums.booleanFilterHasMobileApp" allowClear />
          </Form.Item>
        </div>

        <Flex justify="space-between">
          <ClientExtendedFilterModalForm
            onSubmit={(values) => {
              form.setFieldsValue(values);
              onFilterSubmit(form.getFieldsValue());
            }}
          />

          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};

export default ClientListFilterForm;
